import type { PlatformFieldSchema } from './platform-schema.types';

export type PlatformLevel = 'campaign' | 'ad_group' | 'ad';

export type PlatformFieldKind = 'text' | 'number' | 'currency' | 'date' | 'select' | 'tokens';

export interface PlatformOption {
  value: string;
  label: string;
}

export interface PlatformFieldDef {
  key: string;
  /** The platform's own wording, not ours. "Insertion order", not "ad group". */
  label: string;
  kind: PlatformFieldKind;
  required: boolean;
  options?: PlatformOption[];
  /** Frozen by the platform once the object exists, so an update cannot send it. */
  creationOnly?: boolean;
  /** A sibling key whose value lifts `required` on that row, carrying the same prefix. */
  waivedBy?: string;
  help?: string;
}

export interface PlatformLevelSchema {
  label: string;
  pluralLabel: string;
  fields: PlatformFieldDef[];
}

export interface PlatformStructureSchema {
  /** Publish order, which is also dependency order: a child needs its parent's id. */
  order: PlatformLevel[];
  levels: Record<PlatformLevel, PlatformLevelSchema>;
}

/**
 * Creative fields ride on the ad row, prefixed.
 *
 * A creative is its own object on the platform — an ad only references one — but a
 * media plan is one row per ad, and so is this table. Folding the creative's fields
 * onto the ad needs a prefix because both levels have a `name`, and the prefix is
 * what routes an edit back to the creative rather than the ad.
 */
// eslint-disable-next-line i18next/no-literal-string -- field-key prefix routing an edit back to the creative
export const CREATIVE_FIELD_PREFIX = 'creative.';

export function isCreativeField(key: string): boolean {
  return key.startsWith(CREATIVE_FIELD_PREFIX);
}

export function creativeFieldKey(key: string): string {
  return key.slice(CREATIVE_FIELD_PREFIX.length);
}

/**
 * Keyed loosely on purpose: the server owns this vocabulary, so a kind added there
 * before it is added here must fall back rather than render an undefined control.
 */
const KIND_BY_SCHEMA_KIND: Record<string, PlatformFieldKind> = {
  text: 'text',
  longText: 'text',
  url: 'text',
  number: 'number',
  currency: 'currency',
  date: 'date',
  select: 'select',
  multiSelect: 'tokens',
  list: 'tokens',
};

const FALLBACK_LEVEL: PlatformLevelSchema = { label: '', pluralLabel: '', fields: [] };

const FALLBACK: PlatformStructureSchema = {
  order: ['campaign', 'ad_group', 'ad'],
  levels: { campaign: FALLBACK_LEVEL, ad_group: FALLBACK_LEVEL, ad: FALLBACK_LEVEL },
};

/**
 * Schemas as served by media-api, indexed by platform.
 *
 * Module-level rather than React state because the pure helpers below are called
 * from render bodies and from plain functions that have no hook context. It is
 * populated once by `usePlatformSchemas`, and empty until then — which is why the
 * review screen waits for that query before rendering a table.
 */
let schemasByPlatform: Record<string, PlatformStructureSchema> = {};
let appliedSource: PlatformFieldSchema[] | null = null;

/**
 * Idempotent by payload identity, so a caller may hand the same fetched array over
 * on every render without rebuilding the index.
 */
export function setPlatformSchemas(schemas: PlatformFieldSchema[]): void {
  if (appliedSource === schemas) return;
  appliedSource = schemas;
  schemasByPlatform = Object.fromEntries(
    schemas.map((schema) => [schema.platform, toStructure(schema)]),
  );
}

export function hasPlatformSchemas(): boolean {
  return Object.keys(schemasByPlatform).length > 0;
}

/**
 * Every platform a plan can be written for.
 *
 * A new Job binds all of them, because the sheet is what says which platforms it
 * covers and that is only known once the server has parsed it. The bindings the
 * upload finds no rows for are dropped there.
 */
export function knownPlatforms(): string[] {
  return Object.keys(schemasByPlatform);
}

export function platformStructure(platform: string): PlatformStructureSchema {
  return schemasByPlatform[platform] ?? FALLBACK;
}

export function levelSchema(platform: string, level: PlatformLevel): PlatformLevelSchema {
  return platformStructure(platform).levels[level];
}

/** What this platform calls the tier, e.g. "Insertion order" on DV360. */
export function levelLabel(platform: string, level: PlatformLevel): string {
  return levelSchema(platform, level).label;
}

export function levelPluralLabel(platform: string, level: PlatformLevel): string {
  return levelSchema(platform, level).pluralLabel;
}

export function fieldDef(
  platform: string,
  level: PlatformLevel,
  key: string,
): PlatformFieldDef | undefined {
  return levelSchema(platform, level).fields.find((field) => field.key === key);
}

/**
 * `name` is the row's own title, shown in the line-item column rather than as a
 * field, so it is excluded here and handled by the table directly.
 */
function toFieldDef(field: PlatformFieldSchema['fields'][number], prefix = ''): PlatformFieldDef {
  return {
    key: `${prefix}${field.key}`,
    label: field.label,
    kind: KIND_BY_SCHEMA_KIND[field.kind] ?? 'text',
    required: field.required,
    ...(field.options ? { options: field.options } : {}),
    ...(field.creationOnly ? { creationOnly: true } : {}),
    ...(field.waivedBy ? { waivedBy: `${prefix}${field.waivedBy}` } : {}),
    ...(field.help ? { help: field.help } : {}),
  };
}

function toStructure(schema: PlatformFieldSchema): PlatformStructureSchema {
  const fieldsAt = (level: string, prefix = ''): PlatformFieldDef[] =>
    schema.fields
      .filter((field) => field.level === level)
      .map((field) => toFieldDef(field, prefix));

  const labelsAt = (level: string): { label: string; pluralLabel: string } => {
    const found = schema.levels.find((each) => each.level === level);
    return { label: found?.singular ?? '', pluralLabel: found?.plural ?? '' };
  };

  /* eslint-disable i18next/no-literal-string -- level identifiers matched against the platform schema, not copy; the labels come from the schema itself */
  return {
    order: ['campaign', 'ad_group', 'ad'],
    levels: {
      campaign: { ...labelsAt('campaign'), fields: fieldsAt('campaign') },
      ad_group: { ...labelsAt('adGroup'), fields: fieldsAt('adGroup') },
      ad: {
        ...labelsAt('ad'),
        fields: [...fieldsAt('ad'), ...fieldsAt('creative', CREATIVE_FIELD_PREFIX)],
      },
    },
  };
  /* eslint-enable i18next/no-literal-string */
}
